import { AlertTriangle } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface UnsavedChangesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Confirmação do descarte — sai do formulário perdendo o que não foi salvo. */
  onConfirm: () => void;
  title?: string;
  description?: string;
  /** Rótulo do botão destrutivo (descartar e sair). */
  confirmLabel?: string;
  /** Rótulo do botão seguro (permanecer no formulário). */
  cancelLabel?: string;
}

/**
 * Confirmação de saída com alterações não salvas — substitui o `window.confirm`
 * cru por um `AlertDialog` estilizado. Casca genérica: o texto é parametrizável
 * (criação fala em "preenchimento"; edição, em "alterações"), mas o visual e o
 * comportamento (botão de descarte destrutivo, "continuar editando" como ação
 * segura/padrão) ficam consistentes em todos os formulários.
 *
 * Acionado por `useFormExitGuard`.
 */
export function UnsavedChangesDialog({
  open,
  onOpenChange,
  onConfirm,
  title = "Descartar alterações?",
  description = "Você tem alterações não salvas. Se sair agora, elas serão perdidas.",
  confirmLabel = "Descartar e sair",
  cancelLabel = "Continuar editando",
}: UnsavedChangesDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <span className="mb-1 grid size-11 place-items-center self-center rounded-full bg-destructive/10 text-destructive sm:self-start">
            <AlertTriangle className="size-5" />
          </span>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          {/* Ação segura primeiro/destacada: o foco padrão fica em "continuar editando". */}
          <AlertDialogCancel>{cancelLabel}</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className={cn(
              buttonVariants({ variant: "destructive" }),
              "bg-destructive text-destructive-foreground hover:bg-destructive/90",
            )}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
